import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  type User as FirebaseUser,
} from 'firebase/auth';
import type { CurrentUser, LoginInput, RegisterInput } from '@sirocco/shared';
import { auth } from './app';
import { appeler } from './callable';
import { paths } from './paths';
import { DEFAULT_PRIVACY, toCurrentUser, type UserRecord } from './records';
import { versCarte, versListe } from '../utils/langues';
import { readValue, SiroccoDataError, updateValues, writeValue } from './rtdb';
import { t } from '../i18n';

/**
 * Authentification.
 *
 * Firebase garde les mots de passe et gere la reinitialisation par
 * e-mail ; nous ne stockons que le profil, dans `users/{uid}`. Les deux
 * sont lies par l'identifiant Firebase, qui devient l'identifiant du
 * membre dans toute l'application.
 */

/**
 * Age minimum pour rejoindre Sirocco : 16 ans depuis le 10 octobre 2026,
 * 18 avant. En France, on consent seul au traitement de ses donnees des
 * 15 ans. A tenir d'accord avec les CGU et la politique de confidentialite.
 */
const MIN_AGE = 16;

/**
 * Messages Firebase traduits.
 * On ne distingue jamais "compte inexistant" de "mot de passe faux" :
 * cela reviendrait a confirmer qui est inscrit.
 */
function translate(error: unknown): SiroccoDataError {
  const code = (error as { code?: string })?.code ?? '';
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/user-not-found':
    case 'auth/wrong-password':
      return new SiroccoDataError(t.auth.erreurs.identifiants, code);
    case 'auth/invalid-email':
      return new SiroccoDataError(t.auth.erreurs.emailInvalide, code, 'email');
    case 'auth/email-already-in-use':
      return new SiroccoDataError(t.auth.erreurs.compteExiste, code, 'email');
    case 'auth/weak-password':
      return new SiroccoDataError(t.auth.erreurs.motDePasseFaible, code, 'password');
    case 'auth/too-many-requests':
      return new SiroccoDataError(t.auth.erreurs.tropDeTentatives, code);
    case 'auth/network-request-failed':
      return new SiroccoDataError(t.auth.erreurs.reseau, code);
    default:
      return new SiroccoDataError(t.auth.erreurs.inattendue, code);
  }
}

/**
 * La connexion se fait par adresse e-mail.
 *
 * Se connecter avec un pseudo supposerait de retrouver l'adresse du
 * compte avant meme d'etre authentifie, donc de rendre publique la
 * correspondance pseudo -> e-mail. Ce serait offrir la liste des
 * adresses de tous les membres a qui la demande. Tant qu'il n'y a pas de
 * serveur pour faire cette traduction a l'abri, on demande l'e-mail.
 */
function asEmail(identifier: string): string {
  const trimmed = identifier.trim();
  if (!trimmed.includes('@')) {
    throw new SiroccoDataError(
      t.auth.erreurs.parEmail,
      'auth/needs-email',
      'identifier',
    );
  }
  return trimmed;
}

export async function login(input: LoginInput): Promise<CurrentUser> {
  try {
    const credential = await signInWithEmailAndPassword(
      auth(),
      asEmail(input.identifier),
      input.password,
    );
    return await loadProfile(credential.user);
  } catch (error) {
    if (error instanceof SiroccoDataError) throw error;
    throw translate(error);
  }
}

export async function register(input: RegisterInput): Promise<CurrentUser> {
  if (yearsSince(input.birthDate) < MIN_AGE) {
    throw new SiroccoDataError(t.auth.erreurs.age, 'age', 'birthDate');
  }
  const langues = versListe(versCarte(input.languesParlees));
  if (langues.length === 0) {
    throw new SiroccoDataError(t.commun.uneLangue, 'invalid', 'languesParlees');
  }

  // Le pseudo est reserve avant la creation du compte : deux inscriptions
  // simultanees ne doivent pas aboutir au meme pseudo.
  const usernamePath = paths.username(input.username);
  if (await readValue<string>(usernamePath)) {
    throw new SiroccoDataError(t.auth.erreurs.pseudoPris, 'username-taken', 'username');
  }

  let created: FirebaseUser;
  try {
    const credential = await createUserWithEmailAndPassword(auth(), input.email, input.password);
    created = credential.user;
  } catch (error) {
    throw translate(error);
  }

  const record: UserRecord = {
    username: input.username,
    firstName: input.firstName,
    lastName: input.lastName,
    avatarUrl: null,
    level: 1,
    bio: '',
    email: input.email,
    birthDate: input.birthDate,
    // Un nouveau membre n'a pas encore de spot : il ira le choisir.
    currentServerId: null,
    totalPoints: 0,
    createdAt: Date.now(),
    lastSeenAt: Date.now(),
    privacy: DEFAULT_PRIVACY,
    languesParlees: versCarte(langues),
  };

  await updateValues({
    [paths.user(created.uid)]: record,
    [usernamePath]: created.uid,
  });

  /*
    Le courriel de confirmation part tout de suite.

    Il n'est pas attendu : l'inscription est faite, le compte existe, et
    c'est l'ecran suivant qui demandera de confirmer. Un envoi qui echoue
    — reseau coupe, serveur de courrier muet — ne doit pas defaire une
    inscription reussie ; le bouton « renvoyer » est la pour ca.
  */
  void appeler('envoyerLaVerification', {}).catch(() => undefined);

  return toCurrentUser(created.uid, record);
}

/**
 * Pourquoi rien n'est parti : un courriel tout recent (`repos`), ou trop
 * de demandes pour aujourd'hui (`plein`).
 */
export type RenvoiRetenu = 'repos' | 'plein';

/**
 * Renvoie le courriel de confirmation.
 *
 * Le meme que celui de l'inscription : le lien precedent reste valable,
 * celui-ci le remplace. Rend la raison d'une retenue, `null` si le
 * courriel est parti : une ancienne fonction ne dit rien, et c'est parti.
 */
export async function renvoyerLaVerification(): Promise<RenvoiRetenu | null> {
  const reponse = await appeler<object, { retenu?: RenvoiRetenu } | null>(
    'envoyerLaVerification',
    {},
  );
  return reponse?.retenu ?? null;
}

/**
 * Mon adresse est-elle confirmee, a l'instant ?
 *
 * La reponse vit dans le jeton, qui ne se met pas a jour tout seul : on
 * la redemande a Firebase. C'est ce que fait le bouton « j'ai confirme ».
 */
export async function verifierLAdresse(): Promise<boolean> {
  const membre = auth().currentUser;
  if (!membre) return false;
  await membre.reload();
  return membre.emailVerified;
}

/** L'adresse du compte est-elle confirmee, d'apres ce qu'on sait deja ? */
export function adresseConfirmee(): boolean {
  return auth().currentUser?.emailVerified ?? false;
}

export async function logout(): Promise<void> {
  await signOut(auth());
}

/**
 * Envoie le lien de reinitialisation.
 *
 * Par nos fonctions et non par Firebase : le courriel porte alors la
 * charte de l'application plutot que celle de la console. Le lien, lui,
 * reste fabrique par Firebase — c'est lui qui en verifie le code.
 *
 * La reponse est toujours la meme, adresse connue ou non : dire « ce
 * compte n'existe pas » donnerait a n'importe qui la liste des inscrits.
 */
export async function forgotPassword(email: string): Promise<void> {
  await appeler('envoyerLeMotDePasse', { email: email.trim() });
}

/**
 * Changement de mot de passe.
 * Firebase exige une reconnexion recente : on la refait avec le mot de
 * passe actuel, ce qui verifie du meme coup qu'il est correct.
 */
export async function changePassword(current: string, next: string): Promise<void> {
  const user = auth().currentUser;
  if (!user?.email) throw new SiroccoDataError(t.auth.erreurs.sessionExpiree, 'no-session');
  try {
    await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, current));
    await updatePassword(user, next);
  } catch (error) {
    const code = (error as { code?: string })?.code ?? '';
    if (code === 'auth/invalid-credential' || code === 'auth/wrong-password') {
      throw new SiroccoDataError(t.auth.erreurs.motDePasseActuel, code, 'current');
    }
    throw translate(error);
  }
}

/** Identifiant du membre connecte, ou `null`. */
export function currentUid(): string | null {
  return auth().currentUser?.uid ?? null;
}

/** Identifiant du membre connecte, ou une erreur claire s'il n'y en a pas. */
export function requireUid(): string {
  const uid = currentUid();
  if (!uid) throw new SiroccoDataError(t.auth.erreurs.sessionExpiree, 'no-session');
  return uid;
}

/**
 * Charge le profil lie a un compte Firebase.
 * En profite pour marquer le passage, ce qui alimente le statut
 * "en ligne" des autres ecrans.
 */
export async function loadProfile(user: FirebaseUser): Promise<CurrentUser> {
  const record = await readValue<UserRecord>(paths.user(user.uid));
  if (!record) {
    throw new SiroccoDataError(
      t.auth.erreurs.sansProfil,
      'no-profile',
    );
  }
  void writeValue(paths.userField(user.uid, 'lastSeenAt'), Date.now());
  return toCurrentUser(user.uid, record);
}

/**
 * Previent a chaque changement de session.
 * Firebase restaure la session au demarrage : c'est ce rappel, et non
 * un jeton stocke a la main, qui indique si quelqu'un est connecte.
 */
export function watchSession(
  onChange: (user: CurrentUser | null) => void,
  onError: (message: string) => void,
): () => void {
  return onAuthStateChanged(auth(), (user) => {
    if (!user) {
      onChange(null);
      return;
    }
    loadProfile(user)
      .then(onChange)
      .catch((error: unknown) => {
        onChange(null);
        onError(error instanceof Error ? error.message : t.auth.erreurs.connexion);
      });
  });
}

/** Age en annees revolues a partir d'une date ISO. */
function yearsSince(isoDate: string): number {
  const birth = new Date(isoDate);
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const monthDelta = now.getMonth() - birth.getMonth();
  if (monthDelta < 0 || (monthDelta === 0 && now.getDate() < birth.getDate())) age -= 1;
  return age;
}
